import React from 'react';

import { getCountryCode /* , isSafari */ } from 'wtb-utils';
import { AddStreetAdditional, FieldInfo } from 'components';
import { EXAMPLES, REGEX } from 'utils/validation';

let classes = null;
let translate = null;

const setFieldFunctions = (functions) => {
  if (functions.classes) classes = functions.classes;
  if (functions.translate) translate = functions.translate;
};

// name and label is critical to disable autcomplete in the phone field in Safari browser
// const getName = (name) => (isSafari && name === 'phone' ? 'noAutoFill' : name);
const getCommonFieldProps = (name, isRequired = true) => ({
  variant: 'outlined',
  wrapperClassName: classes.field,
  errorWrapperClassName: classes.fieldError,
  size: 'medium',
  label: `${isRequired ? '*' : ''}${translate(`common.${name}`)}`,
  transforms: [ 'deleteSpaces' ],
  name
});

const getFirstNameField = (firstName) => (
  {
    defaultValue: firstName,
    placeholder: 'e.g. Anna',
    ...getCommonFieldProps('firstName'),
    id: 'personal-info-form-firstname',
    debounce: 2000,
    autoComplete: 'given-name',
    validations: [ {
      type: 'required',
      message: translate('errors.firstName.required')
    },
    {
      type: 'regex',
      data: REGEX.NAME,
      message: translate('errors.firstName.invalidCommaFormat')
    } ]
  }
);
const getLastNameField = (lastName) => (
  {
    ...getCommonFieldProps('lastName'),
    debounce: 2000,
    defaultValue: lastName,
    id: 'personal-info-form-lastname',
    placeholder: 'e.g. Brown',
    autoComplete: 'family-name',
    validations: [ {
      type: 'required',
      message: translate('errors.lastName.required')
    },
    {
      type: 'regex',
      data: REGEX.NAME,
      message: translate('errors.lastName.invalidCommaFormat')
    } ]
  }
);
const getNationalIdField = (nationalId, country) => (
  {
    ...getCommonFieldProps('nationalId'),
    debounce: 2000,
    defaultValue: nationalId,
    id: 'personal-info-form-nationalid',
    placeholder: `e.g. ${EXAMPLES.NATIONALID[country?.toUpperCase()]}`,
    validations: [ {
      type: 'regex',
      data: REGEX.NATIONALID[country?.toUpperCase()],
      message: translate('errors.nationalId.invalid_format', { format: EXAMPLES.NATIONALID[country?.toUpperCase()] })
    }, {
      type: 'required',
      message: translate('errors.nationalId.required')
    } ],
    belowContent: <FieldInfo text={translate('customerInfo.nationalId.info')} />
  }
);

const getEmailField = (customerEmail) => (
  {
    inputType: 'email',
    id: 'personal-info-form-email',
    ...getCommonFieldProps('email'),
    debounce: 2000,
    defaultValue: customerEmail,
    placeholder: 'e.g. name@example.com',
    autoComplete: 'email',
    validations: [ {
      type: 'required',
      message: translate('errors.email.required')
    }, {
      type: 'email',
      message: translate('errors.email.invalid_format')
    } ],
    belowContent: <FieldInfo text={translate('customerInfo.email.info')} />
  }
);
const getPhoneField = (phone, country) => (
  {
    ...getCommonFieldProps('phone'),
    type: 'phone',
    id: 'personal-info-form-phone',
    selectClassName: classes.phoneSelect,
    debounce: 2000,
    autoComplete: 'tel',
    defaultCountry: country?.toUpperCase(),
    preferredCountries: [ 'SE', 'FI', 'NO' ],
    defaultValue: phone,
    placeholder: `e.g. ${EXAMPLES.PHONE[country?.toUpperCase()]}`,
    validations: [ {
      type: 'required',
      message: translate('errors.phone.required')
    }, {
      type: 'phoneCountry',
      message: translate('errors.phone.invalid_format', ' ')
    } ],
    belowContent: <FieldInfo text={translate('customerInfo.phone.info')} />
  }
);

const getStreetField = (street, hasStreetAdditional, addStreetAdditional, country) => (
  {
    ...getCommonFieldProps('street'),
    type: 'places',
    defaultValue: street,
    restrictToCountry: country,
    id: 'personal-info-form-street',
    placeholder: translate('common.street'),
    autoComplete: 'address-line1',
    emptyText: translate('common.placesEmpty'),
    inputProps: {
      'data-testid': 'personal-info-form-street'
    },
    validations: [ {
      type: 'required',
      message: translate('errors.street.required')
    } ],
    belowContent: hasStreetAdditional
      ? null
      : <AddStreetAdditional addStreetAdditional={addStreetAdditional} />
  }
);
const getStreetAdditionalField = (streetAdditional) => (
  {
    ...getCommonFieldProps('streetAdditional', false),
    defaultValue: streetAdditional,
    id: 'personal-info-form-streetAdditional',
    autoComplete: 'address-line2',
    placeholder: translate('common.streetAdditional')
  }
);
const getZipCodeField = (zipCode, country) => (
  {
    ...getCommonFieldProps('zipCode'),
    defaultValue: zipCode,
    id: 'personal-info-form-zipcode',
    placeholder: `e.g. ${EXAMPLES.ZIPCODE[getCountryCode(country)]}`,
    autoComplete: 'postal-code',
    validations: [ {
      type: 'required',
      message: translate('errors.zipCode.required')
    }, {
      type: 'zipCode',
      message: translate('errors.zipCode.invalid_format')
    } ]
  }
);

const getCityField = (city) => (
  {
    ...getCommonFieldProps('city'),
    defaultValue: city,
    id: 'personal-info-form-city',
    placeholder: 'e.g. Stockholm',
    autoComplete: 'address-level2',
    validations: [ {
      type: 'required',
      message: translate('errors.city.required')
    }, {
      type: 'regex',
      data: '^([^0-9]*)$',
      message: translate('errors.city.invalid_format')
    } ]
  }
);
const getCountryField = (country) => (
  {
    ...getCommonFieldProps('country'),
    defaultValue: country,
    id: 'personal-info-form-country',
    // placeholder: 'e.g. Sweden',
    autoComplete: 'country-name',
    transforms: [],
    isClearable: false,
    readOnly: true,
    validations: [],
    helperText: translate('common.countryHelperText')
  }
);

export {
  setFieldFunctions,
  getFirstNameField,
  getLastNameField,
  getNationalIdField,
  getEmailField,
  getPhoneField,
  getStreetField,
  getStreetAdditionalField,
  getZipCodeField,
  getCityField,
  getCountryField
};
